import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { trackPhotoEvent, trackPageView, EVENT_TYPES } from '../utils/analytics';

const SweetMoments = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Utility function to determine photo orientation based on aspect ratio
  const getPhotoOrientation = (width, height) => {
    const aspectRatio = width / height;
    if (aspectRatio > 1.3) return 'horizontal';
    if (aspectRatio < 0.8) return 'vertical';
    if (aspectRatio > 1.1 && aspectRatio < 1.3) return 'large';
    return 'square';
  };

  // Navigation functions for modal
  const goToPrevious = () => {
    if (!selectedImage) return;
    const currentIndex = galleryData.findIndex(item => item.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryData.length - 1;
    setSelectedImage(galleryData[previousIndex]);
  };

  const goToNext = () => {
    if (!selectedImage) return;
    const currentIndex = galleryData.findIndex(item => item.id === selectedImage.id);
    const nextIndex = currentIndex < galleryData.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryData[nextIndex]);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!selectedImage) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      case 'Escape':
        e.preventDefault();
        setSelectedImage(null);
        break;
      default:
        break;
    }
  };

  // Touch/swipe handlers for mobile
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Add keyboard event listener when modal is open
  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track photo gallery page view
  useEffect(() => {
    trackPageView('/sweet-moments', 'Sweet Moments Gallery');
  }, []);

  // Carousel navigation
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryData.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryData.length) % galleryData.length);
  };

  const galleryData = [
    {
      id: 1,
      src: '/images/fotos/foto01.jpeg',
      alt: 'Our First Date',
      title: 'Our First Date',
      date: 'December 25, 2020',
      orientation: 'vertical' // Wide photo
    },
    {
      id: 12,
      src: '/images/fotos/foto12.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical' // Tall photo
    },{
      id: 9,
      src: '/images/fotos/foto09.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'large' // Tall photo
    },
    
    {
      id: 2,
      src: '/images/fotos/foto02.jpeg',
      alt: 'Beach Proposal',
      title: 'Beach Proposal',
      date: 'January 19, 2021',
      orientation: 'square' // Large featured photo
    },{
      id: 8,
      src: '/images/fotos/foto08.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'square' // Tall photo
    },
    
    {
      id: 4,
      src: '/images/fotos/foto04.jpeg',
      alt: 'Engagement Party',
      title: 'Engagement Party',
      date: 'February 14, 2021',
      orientation: 'vertical' // Square photo
    },
    {
      id: 5,
      src: '/images/fotos/foto05.jpeg',
      alt: 'Wedding Planning',
      title: 'Wedding Planning',
      date: 'Planning Our Future',
      orientation: 'large'
    },
    {
      id: 6,
      src: '/images/fotos/foto06.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical-large' // Tall photo
    },{
      id: 11,
      src: '/images/fotos/foto11.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical' // Tall photo
    },{
      id: 10,
      src: '/images/fotos/foto10.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical' // Tall photo
    },
    {
      id: 3,
      src: '/images/fotos/foto03.jpeg',
      alt: 'Coffee Shop Meeting',
      title: 'Coffee Shop Meeting',
      date: 'November 12, 2020',
      orientation: 'vertical' // Tall photo
    },{
      id: 7,
      src: '/images/fotos/foto07.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical' // Tall photo
    }
    ,{
      id: 13,
      src: '/images/fotos/foto13.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical' // Tall photo
    },{
      id: 14,
      src: '/images/fotos/foto14.jpeg',
      alt: 'Together Forever',
      title: 'Together Forever',
      date: 'Our Love Story',
      orientation: 'vertical' // Tall photo
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="gallery" className="py-5  bg-light">
      <div className="container">
        <motion.div 
          className="text-center mb-5"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">{t('sweetMoments.title')}<div className="title-decoration"><img src="/images/section-title.png" alt="Decorative element" /></div></h2>
          <p className="lead text-muted">{t('sweetMoments.subtitle')}</p>
        </motion.div>
        
        {/* Desktop Grid View */}
        {!isMobile && (
          <motion.div 
            className="gallery-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {galleryData.map((item) => (
              <motion.div 
                key={item.id}
                className={`gallery-item ${item.orientation || 'square'}`}
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                        <img 
                          src={item.src} 
                          alt={item.alt} 
                          className="gallery-img"
                          onClick={() => {
                            setSelectedImage(item);
                            trackPhotoEvent(EVENT_TYPES.PHOTO_VIEWED, {
                              photo_id: item.id,
                              photo_title: item.title,
                              photo_orientation: item.orientation,
                              view_type: 'desktop_grid'
                            });
                          }}
                        />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Mobile Carousel View */}
        {isMobile && (
          <motion.div 
            className="gallery-carousel"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="carousel-container">
              <div className="carousel-wrapper">
                <motion.div 
                  className="carousel-track"
                  animate={{ x: -currentSlide * 100 + '%' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                  {galleryData.map((item, index) => {
                    return (
                      <div key={item.id} className="carousel-slide" data-orientation={item.orientation}>
                        <img 
                          src={item.src} 
                          alt={item.alt} 
                          className={`carousel-img ${item.id === 9 ? 'move-right' : ''}`}
                          onClick={() => {
                            setSelectedImage(item);
                            trackPhotoEvent(EVENT_TYPES.PHOTO_VIEWED, {
                              photo_id: item.id,
                              photo_title: item.title,
                              photo_orientation: item.orientation,
                              view_type: 'mobile_carousel'
                            });
                          }}
                        />
                        {/* <div className="carousel-info">
                          <h4>{item.title}</h4>
                          <p>{item.date}</p>
                        </div> */}
                      </div>
                    )
                  })}
                </motion.div>
              </div>
              
              {/* Carousel Navigation */}
              <div className="carousel-nav">
                <button 
                  className="carousel-btn carousel-prev"
                  onClick={prevSlide}
                  aria-label="Previous image"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button 
                  className="carousel-btn carousel-next"
                  onClick={nextSlide}
                  aria-label="Next image"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              
              {/* Carousel Indicators */}
              <div className="carousel-indicators">
                {galleryData.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div 
              className="lightbox-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img src={selectedImage.src} alt={selectedImage.alt} />
              
              {/* Navigation Buttons */}
              <button 
                className="lightbox-nav lightbox-prev"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              
              <button 
                className="lightbox-nav lightbox-next"
                onClick={goToNext}
                aria-label="Next image"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
              
              {/* Close Button */}
              <button 
                className="lightbox-close"
                onClick={() => setSelectedImage(null)}
                aria-label="Close modal"
              >
                <i className="fas fa-times"></i>
              </button>
              
              {/* Image Info */}
              {/* <div className="lightbox-info">
                <h4>{selectedImage.title}</h4>
                <p>{selectedImage.date}</p>
              </div> */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SweetMoments;
